/**
 * Phase 133 — StreamX Polish Sweep
 * Premium reusable empty state: cinematic gradient halo + icon + title + body
 * + optional primary CTA + secondary link. Used across StreamX surfaces wherever
 * there's no data to show. Matches the cinematic dark UI doctrine.
 */
import { Link } from 'react-router-dom';

/**
 * Props:
 *   icon: lucide-react component (required)
 *   title: string (required)
 *   body: string | node (optional)
 *   ctaLabel, ctaTo: optional primary action (internal route)
 *   ctaHref: optional primary action (external link)
 *   ctaIcon: optional lucide icon for the primary button
 *   secondaryLabel, secondaryTo: optional secondary link
 *   accent: hex color for the icon halo (default '#FF5A1F')
 *   testid: optional data-testid hook
 *   compact: true for inline card empty states (no min-height padding)
 */
export default function LivexEmptyState({
  icon: Icon,
  title,
  body,
  ctaLabel,
  ctaTo,
  ctaHref,
  ctaIcon: CtaIcon,
  secondaryLabel,
  secondaryTo,
  accent = '#FF5A1F',
  testid,
  compact = false,
}) {
  const PrimaryCta = ctaLabel ? (
    ctaHref ? (
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid ? `${testid}-cta` : undefined}
        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 active:translate-y-0"
        style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 8px 24px rgba(255,90,31,0.30)', minHeight: '48px' }}
      >
        {CtaIcon && <CtaIcon className="h-4 w-4" />} {ctaLabel}
      </a>
    ) : ctaTo ? (
      <Link
        to={ctaTo}
        data-testid={testid ? `${testid}-cta` : undefined}
        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 active:translate-y-0"
        style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 8px 24px rgba(255,90,31,0.30)', minHeight: '48px' }}
      >
        {CtaIcon && <CtaIcon className="h-4 w-4" />} {ctaLabel}
      </Link>
    ) : null
  ) : null;

  return (
    <div
      data-testid={testid}
      className={`relative ${compact ? 'py-10' : 'py-16 sm:py-20'} px-6 text-center bg-[#07111F] border-2 border-[#22314F] rounded-2xl overflow-hidden`}
    >
      {/* Radial halo backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(55% 50% at 50% 30%, ${accent}26, transparent 60%)`,
        }}
      />
      <div className="relative">
        {/* Icon medallion */}
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
          style={{
            background: `linear-gradient(135deg, ${accent}22, ${accent}08)`,
            border: `2px solid ${accent}55`,
            boxShadow: `0 10px 24px ${accent}22`,
          }}>
          {Icon && <Icon className="h-7 w-7" style={{ color: accent }} />}
        </div>
        <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">{title}</h3>
        {body && (
          <p className="livex-body-text mt-2 max-w-md mx-auto" style={{ fontSize: '14px' }}>{body}</p>
        )}
        {(PrimaryCta || secondaryLabel) && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            {PrimaryCta}
            {secondaryLabel && secondaryTo && (
              <Link
                to={secondaryTo}
                data-testid={testid ? `${testid}-secondary` : undefined}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl transition-all"
              >
                {secondaryLabel}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
